// Terminal management service - Updated to match Postman Collection
import { Terminal } from '../types';
import { getCurrentConfig, getApiScope } from '../config';
import { API_ENDPOINTS, DEFAULT_HEADERS } from '../config/constants';

interface CreateTerminalData {
  terminal_name: string;
  description?: string;
}

interface UpdateTerminalData {
  terminal_name?: string;
  description?: string;
}

class TerminalService {
  private getBaseURL(): string {
    return getCurrentConfig().apiBaseUrl;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const baseUrl = this.getBaseURL();
    
    // Fix double slash issue - simple URL construction
    let url = baseUrl + endpoint;
    url = url.replace(/\/+/g, '/'); // Replace multiple slashes with single slash
    url = url.replace(':/', '://'); // Fix protocol if we accidentally broke it
    
    // Get access token if available (same pattern as api.ts)
    let token: string | null = null;
    try {
      const config = getCurrentConfig();
      
      // Terminal Service Auth Check completed
      
      if (config.auth.enableAuthentication) {
        // Acquiring token for terminal service
        const { msalInstance } = await import('../contexts/AuthContext');
        const accounts = msalInstance.getAllAccounts();
        
        // MSAL accounts available
        
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: [getApiScope()], // Use scope from .env
            account: accounts[0]
          });
          token = tokenResponse.accessToken;
          // Terminal Service: Token acquired
        } else {
          // Terminal Service: No MSAL accounts available
        }
      } else {
        // Terminal Service: Auth disabled
      }
    } catch (error) {
      console.error('❌ Terminal Service: Token acquisition failed:', error);
      // Silent fail for token acquisition
    }
    
    const headers: Record<string, string> = {
      ...DEFAULT_HEADERS,
    };
    
    // Add any additional headers from options
    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          headers[key] = value;
        }
      });
    }
    
    // Add authorization header if token is available
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      // Terminal Service: Added Authorization header
    } else {
      // Terminal Service: No token available, request will be unauthenticated
    }
    
    // Terminal Service: Making request
    
    const response = await fetch(url, {
      headers,
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Get all terminals (legacy method name)
  async getAllTerminals(): Promise<Terminal[]> {
    const response = await this.request<{data: Terminal[]} | Terminal[]>(API_ENDPOINTS.TERMINALS);
    return 'data' in response ? response.data : response;
  }

  // Get all terminals (new method name)
  async getTerminals(): Promise<Terminal[]> {
    const response = await this.request<{data: Terminal[]} | Terminal[]>(API_ENDPOINTS.TERMINALS);
    return 'data' in response ? response.data : response;
  }

  // Get terminal by ID
  async getTerminal(id: number): Promise<Terminal> {
    return this.request<Terminal>(API_ENDPOINTS.TERMINAL_BY_ID(id));
  }

  // Create new terminal
  async createTerminal(data: CreateTerminalData): Promise<Terminal> {
    return this.request<Terminal>(API_ENDPOINTS.TERMINALS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Update terminal
  async updateTerminal(id: number, data: UpdateTerminalData): Promise<Terminal> {
    return this.request<Terminal>(API_ENDPOINTS.TERMINAL_BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Delete terminal
  async deleteTerminal(id: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(API_ENDPOINTS.TERMINAL_BY_ID(id), {
      method: 'DELETE',
    });
  }

  // Validation methods for legacy compatibility
  validateTerminalName(name: string): { isValid: boolean; message?: string } {
    if (!name || name.trim().length < 2) {
      return { isValid: false, message: 'Terminal name must be at least 2 characters long' };
    }
    if (name.length > 100) {
      return { isValid: false, message: 'Terminal name must be less than 100 characters' };
    }
    return { isValid: true };
  }

  validateDescription(description?: string): { isValid: boolean; message?: string } {
    if (description && description.length > 500) {
      return { isValid: false, message: 'Description must be less than 500 characters' };
    }
    return { isValid: true };
  }
}

// Export singleton instance
export const terminalService = new TerminalService();
export default terminalService;
